import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  adminFetchReportingCycles,
  adminUpdateReportingCycle,
} from "../api/reportingCycle.api";
import { UpdateReportingCyclePayload } from "../api/models/reportingCycle.models";
import { useTranslation } from "react-i18next";

const useAdminReportingCycle = ({ companyId, enabled }) => {
  const { t } = useTranslation();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const queryClient = useQueryClient();

  // Fetch reporting cycle data
  const {
    data: reportingCycleData,
    error: reportingCycleError,
    isLoading: isLoadingReportingCycle,
    isSuccess: isReportingCycleSuccess,
  } = useQuery({
    queryKey: ["reportingCycle", companyId],
    queryFn: () => adminFetchReportingCycles(bearerToken as string, companyId),
    enabled: !!enabled,
  });

  const updateReportingCycle = async (payload: UpdateReportingCyclePayload) => {
    const result = await updateReportingCycleMutation.mutateAsync(payload);
    return result;
  };

  const updateReportingCycleMutation = useMutation({
    mutationFn: (payload: UpdateReportingCyclePayload) =>
      adminUpdateReportingCycle(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update reporting cycle:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-reporting-cycle-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-reporting-cycle-updated"),
        life: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
      queryClient.invalidateQueries({
        queryKey: ["models"],
      });
    },
  });

  const isUpdatingReportingCycle = updateReportingCycleMutation.isPending;

  return {
    reportingCycleData,
    reportingCycleError,
    isLoadingReportingCycle,
    isReportingCycleSuccess,
    updateReportingCycle,
    isUpdatingReportingCycle,
  };
};

export default useAdminReportingCycle;
